/* eslint-disable implicit-arrow-linebreak */
/* eslint-disable function-paren-newline */
import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Loading,
  Button,
  Icon,
  Modal,
  ModalFooter,
} from '@folio/stripes/components';
import { FormSpy, useField } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import { FormattedMessage } from 'react-intl';
import { v4 as uuidv4 } from 'uuid';
import HoldingsItems from './HoldingsItems';
import ItemEditModal from './ItemsList/modals/ItemEditModal';
import {
  CreateSingleHoldingModal,
  BatchCreateHoldingsModal,
  BatchEditHoldingsModal,
  BatchDeleteHoldingsModal,
} from './HoldingManagement/modals';
import { HoldingBatchToolbar } from './HoldingManagement/components';

const propTypes = {
  template: PropTypes.object,
  isLoading: PropTypes.bool,
  controlledVocab: PropTypes.object,
  hasError: PropTypes.bool,
  setHasError: PropTypes.func,
};

function ItemsData({
  template,
  isLoading,
  controlledVocab,
  setHasError,
  hasError = false,
}) {
  // Access to holdings and items data fields
  const holdingsField = useField('holdingsData');
  const itemsField = useField('itemsData');
  const classificationField = useField('instanceData.classifications');
  const statisticalCodeIdsField = useField('instanceData.statisticalCodeIds');
  const instanceStatisticalCodeIds = statisticalCodeIdsField?.input?.value || [];

  // State for item edit modal
  const [showItemEditModal, setShowItemEditModal] = useState(false);
  const [selectedItemData, setSelectedItemData] = useState(null);

  // Update holdings callNumber after classification field updated
  useEffect(() => {
    const classification = classificationField?.input?.value?.[0];
    const holdingsArray = holdingsField?.input?.value || [];
    if (!classification || !holdingsArray.length) return;

    const callNumber = `${classification.classificationNumber || ''}${
      classification.authorNumber ? ` ${classification.authorNumber}` : ''
    }`;

    const updatedHoldings = holdingsArray.map((holding) => {
      if (holding.callNumber !== callNumber) {
        return {
          ...holding,
          callNumber: callNumber,
        };
      }
      return holding;
    });

    // Only update if there are actual changes
    const hasChanges = updatedHoldings.some((holding, index) => 
      holding.callNumber !== holdingsArray[index].callNumber
    );

    if (hasChanges) {
      holdingsField.input.onChange(updatedHoldings);
    }
  }, [classificationField?.input?.value, holdingsField]);

  // Update holdings holdingStatisticalCodeIds when instance statisticalCodeIds changes
  useEffect(() => {
    const holdingsArray = holdingsField?.input?.value || [];
    if (!holdingsArray.length) return;

    const newCodes = instanceStatisticalCodeIds;
    const hasChanges = holdingsArray.some(
      (h) => JSON.stringify(h.holdingStatisticalCodeIds || []) !== JSON.stringify(newCodes)
    );
    if (!hasChanges) return;

    holdingsField.input.onChange(
      holdingsArray.map((holding) => ({
        ...holding,
        holdingStatisticalCodeIds: newCodes,
      }))
    );
  }, [JSON.stringify(instanceStatisticalCodeIds)]);

  // State for page-level undo functionality
  const [globalUndoStacks, setGlobalUndoStacks] = useState({});
  const [globalExecuteUndoFunctions, setGlobalExecuteUndoFunctions] = useState(
    {},
  );
  const [showGlobalUndoHistory, setShowGlobalUndoHistory] = useState(false);
  const [currentFormValues, setCurrentFormValues] = useState(null);

  // State for batch operations
  const [selectedHoldings, setSelectedHoldings] = useState([]);
  const [showCreateSingleModal, setShowCreateSingleModal] = useState(false);
  const [showBatchCreateModal, setShowBatchCreateModal] = useState(false);
  const [showBatchEditModal, setShowBatchEditModal] = useState(false);
  const [showBatchDeleteModal, setShowBatchDeleteModal] = useState(false);
  const [isBatchProcessing, setIsBatchProcessing] = useState(false);

  // Check if batch mode is active (any holdings selected)
  const isBatchModeActive = selectedHoldings.length > 0;

  // Callback to collect undo stacks from multiple holdings
  const handleUndoStackUpdate = useCallback(
    (undoStack, holdingId, executeUndo) => {
      console.log(
        'Undo stack update for holding',
        holdingId,
        ':',
        undoStack?.length || 0,
        'items',
      );

      // Use setTimeout to debounce rapid updates
      clearTimeout(window.undoUpdateTimeout);
      window.undoUpdateTimeout = setTimeout(() => {
        setGlobalUndoStacks((prev) => ({
          ...prev,
          [holdingId]: undoStack || [],
        }));

        // Store execute function for each holding
        if (executeUndo) {
          setGlobalExecuteUndoFunctions((prev) => ({
            ...prev,
            [holdingId]: executeUndo,
          }));
        }
      }, 100); // 100ms delay
    },
    [],
  );

  // Handle executing undo from unified modal
  const handleUnifiedUndo = useCallback(
    (action) => {
      const executeFunction = globalExecuteUndoFunctions[action.holdingId];
      if (executeFunction) {
        executeFunction(action);
      }
    },
    [globalExecuteUndoFunctions],
  );

  // Calculate total undo items across all holdings and merge them
  const totalUndoItems = Object.values(globalUndoStacks).reduce(
    (total, stack) => total + stack.length,
    0,
  );

  // Create unified undo list sorted by timestamp with holding info
  const unifiedUndoList = Object.entries(globalUndoStacks)
    .flatMap(([holdingId, stack]) =>
      stack.map((action) => ({ ...action, holdingId })),
    )
    .sort((a, b) => b.timestamp - a.timestamp); // Most recent first

  // Get holding info for display
  const getHoldingDisplayInfo = (holdingId) => {
    // Find holding data from current form values
    const holdingsData = currentFormValues?.holdingsData || [];
    const holdingIndex = holdingsData.findIndex((h) => h.id === holdingId);
    if (holdingIndex >= 0) {
      return {
        index: holdingIndex + 1,
        location: holdingsData[holdingIndex]?.permanentLocationId || '',
      };
    }
    return { index: '?', location: '' };
  };

  // Handle edit item in modal
  const handleEditItem = useCallback(
    (itemIndex, fields, holdingsRecordId, template, controlledVocab) => {
      setSelectedItemData({
        itemIndex,
        fields,
        holdingsRecordId,
        template,
        controlledVocab,
      });
      setShowItemEditModal(true);
    },
    [],
  );

  // Handle save item from modal
  const handleSaveItemFromModal = useCallback(() => {
    // Item data is automatically saved through form state
    // Just close the modal
    setShowItemEditModal(false);
    setSelectedItemData(null);
  }, []);

  // Handle cancel item modal
  const handleCancelItemModal = useCallback(() => {
    setShowItemEditModal(false);
    setSelectedItemData(null);
  }, []);

  // Batch operations handlers
  const handleToggleHoldingSelection = useCallback((holdingId) => {
    setSelectedHoldings((prev) => {
      if (prev.includes(holdingId)) {
        return prev.filter((id) => id !== holdingId);
      } else {
        return [...prev, holdingId];
      }
    });
  }, []);

  const handleSelectAllHoldings = useCallback((holdingsData) => {
    setSelectedHoldings(holdingsData.map((holding) => holding.id));
  }, []);

  const handleClearSelection = useCallback(() => {
    setSelectedHoldings([]);
  }, []);

  const handleBatchCreate = useCallback(() => {
    setShowCreateSingleModal(true);
  }, []);

  const handleShowBatchCreate = useCallback(() => {
    setShowBatchCreateModal(true);
  }, []);

  const handleBatchEdit = useCallback(() => {
    setShowBatchEditModal(true);
  }, []);

  const handleBatchDelete = useCallback(() => {
    setShowBatchDeleteModal(true);
  }, []);

  // Single holding creation handler
  const handleCreateSingleConfirm = useCallback(
    (holdingData) => {
      const holdingId = uuidv4();

      const newHolding = {
        ...holdingData,
        id: holdingId,
        holdingHrid: '',
        // holdingCopyNumber will come from the modal (already calculated)
      };

      // Add new holding to form array
      const currentHoldings = holdingsField.input.value || [];
      holdingsField.input.onChange([...currentHoldings, newHolding]);

      setShowCreateSingleModal(false);
    },
    [holdingsField],
  );

  // Batch operations handlers
  const handleBatchCreateConfirm = useCallback(
    (holdingsToCreate) => {
      // Add all new holdings to form array
      const currentHoldings = holdingsField.input.value || [];
      const updatedHoldings = [...currentHoldings, ...holdingsToCreate];
      holdingsField.input.onChange(updatedHoldings);

      setShowBatchCreateModal(false);
    },
    [holdingsField],
  );

  const handleBatchEditConfirm = useCallback((updatedHoldings, updateItemsLocations = false, editValues = {}) => {
    // Update the form with all updated holdings
    holdingsField.input.onChange(updatedHoldings);

    // Update items locations if requested
    if (updateItemsLocations && itemsField?.input?.value) {
      const currentItemsData = itemsField.input.value;
      const updatedItemsData = { ...currentItemsData };
      
      // Update items for each selected holding
      selectedHoldings.forEach(holdingId => {
        const holdingItems = currentItemsData[holdingId];
        if (holdingItems && holdingItems.length > 0) {
          updatedItemsData[holdingId] = holdingItems.map(item => ({
            ...item,
            permanentLocation: editValues.permanentLocationId ? 
              { id: editValues.permanentLocationId } : item.permanentLocation,
            temporaryLocation: editValues.temporaryLocationId ? 
              { id: editValues.temporaryLocationId } : null
          }));
        }
      });
      
      itemsField.input.onChange(updatedItemsData);
    }

    setShowBatchEditModal(false);
    setSelectedHoldings([]); // Clear selection after edit
  }, [holdingsField, itemsField, selectedHoldings]);

  const handleBatchDeleteConfirm = useCallback(async (holdingIds, shouldDeleteItems = false) => {
    setIsBatchProcessing(true);
    try {
      // Get current holdings and items data
      const currentHoldings = holdingsField.input.value || [];
      const currentItemsData = itemsField.input.value || {};

      // Find indices of holdings to delete (sort in descending order to avoid index shifting)
      const indicesToDelete = holdingIds
        .map(holdingId => currentHoldings.findIndex(holding => holding.id === holdingId))
        .filter(index => index !== -1)
        .sort((a, b) => b - a);

      // If items should be deleted, remove them from the form state
      if (shouldDeleteItems) {
        const updatedItemsData = { ...currentItemsData };
        holdingIds.forEach(holdingId => {
          delete updatedItemsData[holdingId];
        });
        itemsField.input.onChange(updatedItemsData);
      }

      // Create a new array without the deleted holdings
      const updatedHoldings = [...currentHoldings];
      indicesToDelete.forEach(index => {
        updatedHoldings.splice(index, 1);
      });

      // Update the form
      holdingsField.input.onChange(updatedHoldings);

      setShowBatchDeleteModal(false);
      setSelectedHoldings([]); // Clear selection after delete
    } catch (error) {
      console.error('Error deleting holdings:', error);
    } finally {
      setIsBatchProcessing(false);
    }
  }, [holdingsField, itemsField]);

  // Modal close handlers
  const handleCloseBatchModals = useCallback(() => {
    if (!isBatchProcessing) {
      setShowBatchCreateModal(false);
      setShowBatchEditModal(false);
      setShowBatchDeleteModal(false);
    }
  }, [isBatchProcessing]);

  const handleCloseSingleModal = useCallback(() => {
    setShowCreateSingleModal(false);
  }, []);

  if (isLoading) {
    return <Loading size="large" />;
  }

  return (
    <>
      <div
        style={{
          paddingLeft: '200px',
          paddingRight: '200px',
        }}
      >
        <FormSpy subscription={{ values: true }}>
          {({ values }) => {
            const holdingsData = values?.holdingsData || [];

            return (
              <>
                {/* Batch Mode Warning */}
                {isBatchModeActive && (
                  <div style={{
                    backgroundColor: '#fff3cd',
                    padding: '12px',
                    margin: '10px 0',
                    border: '1px solid #fbbf24',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                  >
                    <FormattedMessage
                      id="ui-plugin-inventory-templates.batchMode.warning"
                      values={{ count: selectedHoldings.length }}
                      defaultMessage="Batch mode: {count} holdings selected. Other operations are disabled until selection is cleared."
                    />
                    <Button
                      buttonStyle="primary"
                      onClick={handleClearSelection}
                      marginBottom0
                    >
                      <FormattedMessage
                        id="ui-plugin-inventory-templates.batchMode.clearSelection"
                        defaultMessage="Clear selection"
                      />
                    </Button>
                  </div>
                )}

                {/* Holdings Batch Toolbar */}
                <HoldingBatchToolbar
                  holdingsData={holdingsData}
                  selectedHoldings={selectedHoldings}
                  totalUndoItems={totalUndoItems}
                  isBatchModeActive={isBatchModeActive}
                  onSelectAllHoldings={handleSelectAllHoldings}
                  onClearSelection={handleClearSelection}
                  onBatchCreate={handleBatchCreate}
                  onShowBatchCreate={handleShowBatchCreate}
                  onBatchEdit={handleBatchEdit}
                  onBatchDelete={handleBatchDelete}
                  onShowUndoHistory={() => setShowGlobalUndoHistory(true)}
                />
              </>
            );
          }}
        </FormSpy>
        <FormSpy subscription={{ values: true }}>
          {({ values }) => {
            // Update current form values for holding info
            if (values && values !== currentFormValues) {
              setCurrentFormValues(values);
            }

            return (
              <FieldArray
                name="holdingsData"
                subscription={{ value: true }}
                render={({ fields: holdingsFields }) => {
                  const holdingsData = values?.holdingsData || [];

                  return (
                    <HoldingsItems
                      template={template}
                      holdingsData={holdingsData}
                      controlledVocab={controlledVocab}
                      setHasError={setHasError}
                      hasError={hasError}
                      onEditItem={handleEditItem}
                      onUndoStackUpdate={handleUndoStackUpdate}
                      holdingsFields={holdingsFields} // Pass holdings fields for management
                      createUndoAction={null} // TODO: Implement holding undo actions
                      // Batch operations props
                      selectedHoldings={selectedHoldings}
                      onToggleSelection={handleToggleHoldingSelection}
                      isBatchModeActive={isBatchModeActive}
                    />
                  );
                }}
              />
            );
          }}
        </FormSpy>
      </div>

      {/* Item Edit Modal */}
      {selectedItemData && (
        <ItemEditModal
          isOpen={showItemEditModal}
          itemIndex={selectedItemData.itemIndex}
          fields={selectedItemData.fields}
          template={selectedItemData.template}
          controlledVocab={selectedItemData.controlledVocab}
          holdingsRecordId={selectedItemData.holdingsRecordId}
          onSave={handleSaveItemFromModal}
          onCancel={handleCancelItemModal}
          onClose={handleCancelItemModal}
        />
      )}

      {/* Unified Undo History Modal */}
      {showGlobalUndoHistory && (
        <Modal
          open={showGlobalUndoHistory}
          onClose={() => setShowGlobalUndoHistory(false)}
          label={
            <FormattedMessage
              id="ui-plugin-inventory-templates.undoHistory.modal.title"
              defaultMessage="Operations History"
            />
          }
          size="medium"
        >
          <div style={{ padding: '20px' }}>
            <h4 style={{ marginBottom: '8px' }}>
              <FormattedMessage
                id="ui-plugin-inventory-templates.undoHistory.modal.header"
                defaultMessage="Recent Operations History"
              />
            </h4>

            {unifiedUndoList.length > 0 && (
              <p
                style={{
                  fontSize: '14px',
                  color: '#6b7280',
                  marginBottom: '16px',
                  backgroundColor: '#f0f9ff',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: '1px solid #bfdbfe',
                }}
              >
                <FormattedMessage
                  id="ui-plugin-inventory-templates.undoHistory.modal.instructions"
                  defaultMessage="You can undo multiple operations consecutively - the window will remain open until you close it manually"
                />
              </p>
            )}

            {unifiedUndoList.length === 0 ? (
              <div>
                <div
                  style={{
                    textAlign: 'center',
                    color: '#6c757d',
                    padding: '40px',
                  }}
                >
                  <FormattedMessage
                    id="ui-plugin-inventory-templates.undoHistory.modal.empty"
                    defaultMessage="No operations in history"
                  />
                </div>
                <div
                  style={{
                    textAlign: 'center',
                    color: '#16a34a',
                    fontSize: '14px',
                    fontWeight: '500',
                    backgroundColor: '#f0fdf4',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    margin: '0 20px',
                    border: '1px solid #bbf7d0',
                  }}
                >
                  <FormattedMessage
                    id="ui-plugin-inventory-templates.undoHistory.modal.emptyAction"
                    defaultMessage="You can close this window and continue"
                  />
                </div>
              </div>
            ) : (
              <div>
                {unifiedUndoList.map((action, index) => {
                  const holdingInfo = getHoldingDisplayInfo(action.holdingId);
                  return (
                    <div
                      key={action.id}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '12px',
                        backgroundColor: index === 0 ? '#e8f5e8' : '#f8f9fa',
                        border: '1px solid #dee2e6',
                        borderRadius: '6px',
                        marginBottom: '8px',
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: '600', fontSize: '14px' }}>
                          <FormattedMessage
                            id={`ui-plugin-inventory-templates.${action.description}`}
                            values={{
                              volume: action.data?.volume || '',
                              newVolume: action.data?.newVolumeString || '',
                              count:
                                action.data?.deletedItems?.length ||
                                action.data?.allItems?.length ||
                                action.data?.originalItems?.length ||
                                0,
                            }}
                            defaultMessage={
                              action.description ===
                              'undoSystem.actions.deleteItem'
                                ? 'Delete item'
                                : action.description ===
                                    'undoSystem.actions.deleteItems'
                                  ? 'Delete {count} item(s)'
                                  : action.description ===
                                      'undoSystem.actions.deleteVolume'
                                    ? 'Delete volume "{volume}" ({count} items)'
                                    : action.description ===
                                        'undoSystem.actions.deleteAllVolumes'
                                      ? 'Delete all volumes ({count} items)'
                                      : action.description ===
                                          'undoSystem.actions.resetVolume'
                                        ? 'Reset volume "{volume}" to "{newVolume}"'
                                        : action.description
                            }
                          />
                        </div>
                        <div
                          style={{
                            fontSize: '12px',
                            color: '#6c757d',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                          }}
                        >
                          <span>
                            {new Date(action.timestamp).toLocaleTimeString(
                              'ar-SA',
                            )}
                          </span>
                          <span
                            style={{
                              backgroundColor: '#e3f2fd',
                              color: '#1976d2',
                              padding: '2px 6px',
                              borderRadius: '10px',
                              fontSize: '11px',
                              fontWeight: '500',
                            }}
                          >
                            <FormattedMessage
                              id="ui-plugin-inventory-templates.undoHistory.modal.holdingInfo"
                              values={{ index: holdingInfo.index }}
                              defaultMessage="Holding {index}"
                            />
                          </span>
                        </div>
                      </div>

                      <Button
                        buttonStyle={index === 0 ? 'primary' : 'default'}
                        onClick={() => {
                          handleUnifiedUndo(action);
                        }}
                        marginBottom0
                        disabled={index !== 0}
                      >
                        {index === 0 ? (
                          <FormattedMessage
                            id="ui-plugin-inventory-templates.undoHistory.modal.undoButton"
                            defaultMessage="Undo"
                          />
                        ) : (
                          <FormattedMessage
                            id="ui-plugin-inventory-templates.undoHistory.modal.doneButton"
                            defaultMessage="Done"
                          />
                        )}
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <ModalFooter>
            <Button
              onClick={() => setShowGlobalUndoHistory(false)}
              marginBottom0
              buttonStyle="primary"
            >
              <FormattedMessage
                id="ui-plugin-inventory-templates.undoHistory.modal.closeButton"
                defaultMessage="Close"
              />
            </Button>

            {unifiedUndoList.length > 0 && (
              <Button
                buttonStyle="danger"
                onClick={() => {
                  // Clear all undo stacks
                  Object.keys(globalUndoStacks).forEach((holdingId) => {
                    const executeFunction =
                      globalExecuteUndoFunctions[holdingId];
                    if (executeFunction && executeFunction.clearUndoStack) {
                      executeFunction.clearUndoStack();
                    }
                  });
                  setGlobalUndoStacks({});
                  setShowGlobalUndoHistory(false);
                }}
                marginBottom0
              >
                <FormattedMessage
                  id="ui-plugin-inventory-templates.undoHistory.modal.clearAllButton"
                  defaultMessage="Clear All & Close"
                />
              </Button>
            )}
          </ModalFooter>
        </Modal>
      )}

      {/* Create Single Holding Modal */}
      {showCreateSingleModal && (
        <CreateSingleHoldingModal
          isOpen={showCreateSingleModal}
          onClose={handleCloseSingleModal}
          onConfirm={handleCreateSingleConfirm}
          template={template}
          controlledVocab={controlledVocab}
          currentHoldingsCount={holdingsField?.input?.value?.length || 0}
          instanceStatisticalCodeIds={instanceStatisticalCodeIds}
        />
      )}

      {/* Batch Create Holdings Modal */}
      {showBatchCreateModal && (
        <BatchCreateHoldingsModal
          isOpen={showBatchCreateModal}
          onClose={handleCloseBatchModals}
          onConfirm={handleBatchCreateConfirm}
          template={template}
          controlledVocab={controlledVocab}
          currentHoldingsCount={holdingsField?.input?.value?.length || 0}
          isProcessing={isBatchProcessing}
          instanceStatisticalCodeIds={instanceStatisticalCodeIds}
        />
      )}

      {/* Batch Edit Holdings Modal */}
      {showBatchEditModal && currentFormValues?.holdingsData && (
        <BatchEditHoldingsModal
          isOpen={showBatchEditModal}
          onClose={handleCloseBatchModals}
          onConfirm={handleBatchEditConfirm}
          selectedHoldings={selectedHoldings}
          holdingsData={currentFormValues.holdingsData}
          controlledVocab={controlledVocab}
          template={template}
          itemsFields={{ value: Object.values(itemsField?.input?.value || {}).flat() }}
        />
      )}

      {/* Batch Delete Holdings Modal */}
      {showBatchDeleteModal &&
        currentFormValues?.holdingsData &&
        currentFormValues?.itemsData && (
          <BatchDeleteHoldingsModal
            isOpen={showBatchDeleteModal}
            onClose={handleCloseBatchModals}
            onConfirm={handleBatchDeleteConfirm}
            selectedHoldings={selectedHoldings}
            holdingsData={currentFormValues.holdingsData}
            itemsData={currentFormValues.itemsData}
            controlledVocab={controlledVocab}
            isProcessing={isBatchProcessing}
          />
      )}
    </>
  );
}

ItemsData.propTypes = propTypes;

export default ItemsData;
